
import { supabase } from "@/integrations/supabase/client";
import { sendNotification, requestNotificationPermission } from "./desktopNotifications";
import { showToast } from "./toastService";
import { format, isAfter, isBefore, startOfDay, endOfDay } from "date-fns";
import { fr } from "date-fns/locale";

interface NotificationContext {
  userId: string;
  userRole: "admin" | "ouvrier";
  currentLocation?: {
    latitude: number;
    longitude: number;
  };
  activeWorksite?: string;
  isTracking: boolean;
}

interface IntelligentNotification {
  id: string;
  type: "reminder" | "alert" | "info" | "warning";
  priority: "low" | "medium" | "high" | "critical";
  title: string;
  message: string;
  actionRequired: boolean;
  expiresAt?: Date;
  context: string[];
}

export class IntelligentNotificationService {
  private context: NotificationContext | null = null;
  private scheduledNotifications: Map<string, NodeJS.Timeout> = new Map();
  private lastNotificationTimes: Map<string, number> = new Map();
  private readonly NOTIFICATION_COOLDOWN = 5 * 60 * 1000; // 5 minutes

  public setContext(context: NotificationContext) {
    this.context = context;
    console.log('📱 [Intelligent Notifications] Contexte mis à jour:', context);
  }

  public async checkAndSendNotifications(): Promise<void> {
    if (!this.context) return;

    const notifications = await this.generateContextualNotifications();
    
    for (const notification of notifications) {
      if (this.shouldSendNotification(notification)) {
        await this.sendIntelligentNotification(notification);
      }
    }
  }

  private async generateContextualNotifications(): Promise<IntelligentNotification[]> {
    if (!this.context) return [];

    const notifications: IntelligentNotification[] = [];
    const now = new Date();

    // 1. Notifications de pointage intelligent
    if (this.context.userRole === "ouvrier") {
      const pointageNotifications = await this.generatePointageNotifications(now);
      notifications.push(...pointageNotifications);
    }

    // 2. Notifications d'alertes métier
    const businessNotifications = await this.generateBusinessNotifications();
    notifications.push(...businessNotifications);

    // 3. Notifications de localisation
    const locationNotifications = await this.generateLocationNotifications();
    notifications.push(...locationNotifications);

    // 4. Notifications d'inactivité
    const inactivityNotifications = await this.generateInactivityNotifications();
    notifications.push(...inactivityNotifications);

    return notifications;
  }

  private async generatePointageNotifications(now: Date): Promise<IntelligentNotification[]> {
    const notifications: IntelligentNotification[] = [];
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Rappel de début de journée (8h00-8h30)
    if (currentHour === 8 && currentMinute >= 0 && currentMinute <= 30 && !this.context?.isTracking) {
      notifications.push({
        id: 'morning-reminder',
        type: 'reminder',
        priority: 'medium',
        title: 'Rappel de pointage',
        message: 'Il est temps de commencer votre journée de travail. N\'oubliez pas de pointer votre arrivée.',
        actionRequired: true,
        context: ['pointage', 'morning'],
        expiresAt: new Date(now.getTime() + 30 * 60 * 1000) // Expire dans 30min
      });
    }

    // Rappel si pas de pointage après 9h
    if (currentHour >= 9 && !this.context?.isTracking) {
      const todayEntries = await this.getTodayTimeEntries();
      if (todayEntries.length === 0) {
        notifications.push({
          id: 'late-start-alert',
          type: 'alert',
          priority: 'high',
          title: 'Pointage en retard',
          message: 'Vous n\'avez pas encore pointé aujourd\'hui. Pensez à enregistrer votre début de journée.',
          actionRequired: true,
          context: ['pointage', 'late']
        });
      }
    }

    // Rappel de fin de journée (17h00-18h00)
    if (currentHour >= 17 && currentHour < 18 && this.context?.isTracking) {
      notifications.push({
        id: 'evening-reminder',
        type: 'reminder',
        priority: 'medium',
        title: 'Fin de journée',
        message: 'Il est temps de pointer la fin de votre journée de travail.',
        actionRequired: true,
        context: ['pointage', 'evening']
      });
    }

    // Alerte pour journée très longue (plus de 10h)
    if (this.context?.isTracking) {
      const workDuration = await this.getCurrentWorkDuration();
      if (workDuration > 10) {
        notifications.push({
          id: 'overtime-alert',
          type: 'warning',
          priority: 'high',
          title: 'Heures supplémentaires',
          message: `Vous travaillez depuis ${Math.round(workDuration)}h aujourd'hui. Pensez à faire une pause.`,
          actionRequired: false,
          context: ['overtime', 'health']
        });
      }
    }

    return notifications;
  }

  private async generateBusinessNotifications(): Promise<IntelligentNotification[]> {
    const notifications: IntelligentNotification[] = [];

    // Vérifier les chantiers sans activité
    const inactiveWorksites = await this.getInactiveWorksites();
    if (inactiveWorksites.length > 0 && this.context?.userRole === "admin") {
      notifications.push({
        id: 'inactive-worksites',
        type: 'info',
        priority: 'medium',
        title: 'Chantiers inactifs',
        message: `${inactiveWorksites.length} chantier(s) sans activité récente détecté(s).`,
        actionRequired: true,
        context: ['admin', 'worksites']
      });
    }

    // Vérifier les ouvriers sans assignation
    if (this.context?.userRole === "admin") {
      const unassignedWorkers = await this.getUnassignedWorkers();
      if (unassignedWorkers.length > 0) {
        notifications.push({
          id: 'unassigned-workers',
          type: 'warning',
          priority: 'high',
          title: 'Ouvriers non assignés',
          message: `${unassignedWorkers.length} ouvrier(s) sans assignation pour la semaine prochaine.`,
          actionRequired: true,
          context: ['admin', 'assignments']
        });
      }
    }

    return notifications;
  }

  private async generateLocationNotifications(): Promise<IntelligentNotification[]> {
    const notifications: IntelligentNotification[] = [];

    if (this.context?.currentLocation && this.context?.activeWorksite && this.context?.isTracking) {
      const distance = await this.getDistanceFromWorksite();
      
      if (distance > 500) { // Plus de 500m du chantier
        notifications.push({
          id: 'location-alert',
          type: 'warning',
          priority: 'medium',
          title: 'Position éloignée',
          message: `Vous êtes à ${Math.round(distance)}m de votre chantier assigné.`,
          actionRequired: false,
          context: ['location', 'worksite']
        });
      }
    }

    return notifications;
  }

  private async generateInactivityNotifications(): Promise<IntelligentNotification[]> {
    const notifications: IntelligentNotification[] = [];

    if (this.context?.isTracking) {
      const lastActivity = await this.getLastActivity();
      const inactiveMinutes = (Date.now() - lastActivity.getTime()) / (1000 * 60);

      if (inactiveMinutes > 120) { // Plus de 2h d'inactivité
        notifications.push({
          id: 'inactivity-alert',
          type: 'info',
          priority: 'low',
          title: 'Inactivité détectée',
          message: 'Aucune activité détectée depuis plus de 2 heures. Êtes-vous en pause ?',
          actionRequired: true,
          context: ['activity', 'break']
        });
      }
    }

    return notifications;
  }

  private shouldSendNotification(notification: IntelligentNotification): boolean {
    const now = Date.now();
    const lastSent = this.lastNotificationTimes.get(notification.id) || 0;
    
    // Vérifier le cooldown
    if (now - lastSent < this.NOTIFICATION_COOLDOWN) {
      return false;
    }

    // Vérifier si la notification a expiré
    if (notification.expiresAt && isAfter(now, notification.expiresAt)) {
      return false;
    }

    return true;
  }

  private async sendIntelligentNotification(notification: IntelligentNotification): Promise<void> {
    const now = Date.now();
    
    // Marquer comme envoyée
    this.lastNotificationTimes.set(notification.id, now);

    // Choisir le type de notification selon la priorité
    if (notification.priority === 'critical' || notification.priority === 'high') {
      // Notification bureau + toast
      await sendNotification(notification.title, {
        body: notification.message,
        tag: notification.id,
        requireInteraction: notification.actionRequired
      }, notification.type);
    }

    // Toast pour tous les niveaux
    showToast(
      notification.title,
      notification.message,
      notification.type === 'alert' ? 'warning' : notification.type === 'reminder' ? 'info' : notification.type,
      notification.priority === 'high' ? 10000 : 5000
    );

    console.log(`📱 [Intelligent Notifications] Envoyée: ${notification.title} (${notification.priority})`);
  }

  // Méthodes utilitaires
  private async getTodayTimeEntries() {
    if (!this.context?.userId) return [];

    const today = format(new Date(), 'yyyy-MM-dd');
    const { data } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', this.context.userId)
      .eq('date', today);

    return data || [];
  }

  private async getCurrentWorkDuration(): Promise<number> {
    const todayEntries = await this.getTodayTimeEntries();
    if (todayEntries.length === 0) return 0;

    const entry = todayEntries[0];
    if (!entry.start_time) return 0;

    const startTime = new Date(`1970-01-01T${entry.start_time}`);
    const endTime = entry.end_time ? 
      new Date(`1970-01-01T${entry.end_time}`) : 
      new Date();

    return (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
  }

  private async getInactiveWorksites() {
    const threeDaysAgo = format(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
    
    const { data } = await supabase
      .from('worksites')
      .select(`
        id, name,
        time_entries!inner(date)
      `)
      .eq('status', 'active')
      .lt('time_entries.date', threeDaysAgo);

    return data || [];
  }

  private async getUnassignedWorkers() {
    // Logique pour récupérer les ouvriers non assignés
    // Cette logique existe déjà dans assignmentCheckService
    return [];
  }

  private async getDistanceFromWorksite(): Promise<number> {
    // Calcul de distance simplifiée - à améliorer avec une vraie API de géolocalisation
    return Math.random() * 1000; // Simulation
  }

  private async getLastActivity(): Promise<Date> {
    // Récupérer la dernière activité de l'utilisateur
    return new Date(Date.now() - Math.random() * 3 * 60 * 60 * 1000); // Simulation
  }

  public scheduleNotification(id: string, delay: number, callback: () => void): void {
    if (this.scheduledNotifications.has(id)) {
      clearTimeout(this.scheduledNotifications.get(id)!);
    }

    const timeout = setTimeout(() => {
      callback();
      this.scheduledNotifications.delete(id);
    }, delay);

    this.scheduledNotifications.set(id, timeout);
  }

  public cancelScheduledNotification(id: string): void {
    if (this.scheduledNotifications.has(id)) {
      clearTimeout(this.scheduledNotifications.get(id)!);
      this.scheduledNotifications.delete(id);
    }
  }

  public cleanup(): void {
    // Nettoyer toutes les notifications programmées
    this.scheduledNotifications.forEach(timeout => clearTimeout(timeout));
    this.scheduledNotifications.clear();
  }
}

// Instance globale
export const intelligentNotificationService = new IntelligentNotificationService();
